import type { ProfilesClient } from '@customink/profiles-sso-spa-client';
import Cookies from 'cookies-js';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { config } from '@/config/api.config';
import { auth0Client } from '@/contexts/auth/auth0Client';
import type { AuthContextData } from '@/contexts/auth/AuthContext';
import { AuthContext, getTargetUserInfo } from '@/contexts/auth/AuthContext';
import { profilesClient } from '@/contexts/auth/profilesClient';
import { AUTH0_ROLLOUT_FLAG_NAME } from '@/utils/featureFlags';
import { eraseSearchParams } from '@/utils/url';

type Auth0Client = typeof auth0Client;

const getAuthContextDefaultData = (
  client: ProfilesClient | Auth0Client,
): AuthContextData => ({
  user: null,
  personalAccount: null,
  tokens: null,
  isLoading: true,
  login: {
    loginWithRedirect: () => client.loginWithRedirect(),
    loginWithPopup: () => client.loginWithPopup().then(),
    loginSilently: () => client.loginSilently().then(),
  },
});

export const AuthProvider: FC = ({ children }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const impersonate = searchParams.get('impersonate');

  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isAccountLoading, setIsAccountLoading] = useState(true);

  const [user, setUser] = useState<AuthContextData['user']>(null);
  const [tokens, setTokens] = useState<AuthContextData['tokens']>(null);
  const [personalAccount, setPersonalAccount] =
    useState<AuthContextData['personalAccount']>(null);

  const isAuth0Enabled = Cookies.get(AUTH0_ROLLOUT_FLAG_NAME) === 'test';
  const client = isAuth0Enabled ? auth0Client : profilesClient;

  const providerValue = useMemo<AuthContextData>(
    (): AuthContextData => ({
      ...getAuthContextDefaultData(client),
      user,
      tokens,
      personalAccount,
      isLoading: isAuthLoading || isAccountLoading,
    }),
    [user, tokens, personalAccount, isAuthLoading, isAccountLoading],
  );

  useEffect(() => {
    const initializeClient = async () => {
      client.subscribe(
        ({ isAuthenticated, userInfo, accessToken, idToken }) => {
          if (!isAuthenticated) {
            return;
          }

          const isImpersonate = Boolean(impersonate && userInfo?.internal);

          setUser({
            id: userInfo?.sub ?? '',
            isInternal: Boolean(userInfo?.internal),
            isLoggedIn: isAuthenticated,
            ...getTargetUserInfo(
              isImpersonate,
              userInfo?.email ?? '',
              impersonate,
            ),
            emailDerivedName: userInfo?.email?.split('@')[0] ?? '',
            fullName: userInfo?.name?.trim().split('@')[0] ?? '', // the split on '@' patches a profiles bug where sometimes this field wrongly contains email instead of null.
          });

          setTokens({
            idToken: idToken ?? '',
            accessToken: accessToken ?? '',
          });
        },
      );

      await client.start();
      setIsAuthLoading(false);
      eraseSearchParams('code', 'state');
    };

    initializeClient();
  }, [client]);

  useEffect(() => {
    const authToken = isAuth0Enabled ? tokens?.accessToken : tokens?.idToken;
    const getAccountIdFromUser = async () => {
      // Intentionally using fetch and not RTK to avoid entangling Auth with project-service concerns.
      // AuthProvider is considered closed domain and might be extracted later.
      const queryParams = user?.email
        ? new URLSearchParams({
            email: user.email,
          }).toString()
        : '';

      const response = await fetch(
        `${config.accountsServiceUrl}/accounts/personal` +
          (queryParams ? `?${queryParams}` : ''),
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: 'application/json',
          },
        },
      );
      const data = await response.json();
      setPersonalAccount({ id: data.id });
    };
    if (!isAuthLoading && isAccountLoading) {
      if (user !== null && tokens !== null && Boolean(authToken)) {
        getAccountIdFromUser().finally(() => setIsAccountLoading(false));
      } else {
        setIsAccountLoading(false);
      }
    }
  }, [isAuthLoading, user, tokens]);

  return (
    <AuthContext.Provider value={providerValue}>
      {children}
    </AuthContext.Provider>
  );
};
